// src/context/ActivityContext.jsx
import { createContext, useReducer, useEffect, useState, useCallback, useMemo, useContext } from "react";
import { vfInteract, vfUpdateVariables, vfFetchState } from "../api/VoiceflowInteractions";
import { AuthContext } from "./AuthContext";
import { HistoryContext } from "./HistoryContext";
import { ProsContext } from "./ProsContext";
import { createActivity, addMessage, fetchActivities } from "../api/activity";
import { updateLuxMetiersVariables } from "../utils/luxVariables";

export const ActivityContext = createContext({
  activityConvs: {},
  sendToVF: () => Promise.resolve(),
  initSessionActivity: async () => {},
  resetSession: () => {},
});
function reducerActivity(state = {}, action) {
  const { type, jobId, payload, sessionSlug, title, photo_url, activityType, made_for_you, score } = action;
  switch (type) {
    case "ADD_MSG":
      return {
        ...state,
        [`${jobId}/${activityType}`]: {
          sessionSlug: state[`${jobId}/${activityType}`].sessionSlug,
          messages: [...(state[`${jobId}/${activityType}`].messages || []), payload],
          title: state[`${jobId}/${activityType}`].title,
          photo_url: state[`${jobId}/${activityType}`].photo_url,
          made_for_you: state[`${jobId}/${activityType}`].made_for_you,
          score : state[`${jobId}/${activityType}`].score || score
        },
      };
    case "INIT_SESSION":
      if (!jobId || !activityType) return state;

     return state[`${jobId}/${activityType}`]                                  
       ? state                                       
       : { ...state, [`${jobId}/${activityType}`]: {sessionSlug: sessionSlug, messages: [], title: title, photo_url: photo_url, made_for_you: made_for_you, score} };
    case "RESET_SESSION":
      return {}
    default:
      return state;
  }
}

// lazy init : ne s’exécute qu’une fois, avant le 1er rendu
function initActivities() {
  try {
    const saved = localStorage.getItem("activities");
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
}

export function ActivityProvider({ children }) {
  const [activityConvs, dispatchActivity] = useReducer(reducerActivity, {}, initActivities);
  const { user } = useContext(AuthContext);
  const { addAction, history } = useContext(HistoryContext);
  const { itemsPro } = useContext(ProsContext);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      resetSession()
    } else {
      // Loading activities
      (async () => {
        try {
          const activities = await fetchActivities(user.authToken)
          activities.forEach( (activity) => {
            const gender = Math.random() < 0.5 ? 'm' : 'f';
            if (activityConvs[`${activity.ciaov2_job_id}/${activity.type}`]) return;
            const made_for_you = user.age < 16 ? activity.job['made_for_you_14_v2'] : activity.job['made_for_you_16_v2']
            dispatchActivity({
              type: "INIT_SESSION",
              jobId: activity.ciaov2_job_id,
              activityType: activity.type,
              payload: {},
              sessionSlug: activity.sessionSlug,
              title: activity.job[`title_${gender}`],
              photo_url: activity.job[`aws_pictures_${gender}`][1],
              made_for_you: made_for_you.map((item) => item.text).join("\n")
            });
            activity.messages.forEach( (message ) => {
              dispatchActivity({
                type: "ADD_MSG",
                jobId: activity.ciaov2_job_id,
                activityType: activity.type,
                payload: message
              });
            })
          })
        } catch (err) {
          console.error("initSession failed:", err);
        }
      })();
    }
  }, [user?.id]);

  // persistance à CHAQUE changement de convs
  useEffect(() => {
    localStorage.setItem("activities", JSON.stringify(activityConvs));
  }, [activityConvs]);

  const resetSession = useCallback (
    async () => {
      dispatchActivity({
        type: "RESET_SESSION"
      })
    }
  )

  // action pour initialiser la session vide
  const initSessionActivity = useCallback(
    async (values, apiKey) => {
      if (activityConvs[`${values.jobId}/${values.type}`])
        return

      let initValues = {
        "CIAO_bot_firstname": values.name,
        "CIAO_user_firstname": user.firstName,
        "CIAO_user_gender_en": user.gender,
        "CIAO_user_age": user.age,
        "CIAO_job_title": values.title,
        "CIAO_job_key_traits": values.made_for_you
      }
      setIsWaitingForResponse(true)

      const sessionSlug = Math.random().toString(36).substring(2, 15)
      dispatchActivity({ 
        type: "INIT_SESSION",
        jobId: values.jobId,
        activityType: values.type,
        payload: {}, sessionSlug,
        title: values.title,
        photo_url: values.photo_url,
        made_for_you: values.made_for_you
      });

      await createActivity(
        {
          title: values.title,
          sessionSlug,
          message: [],
          jobId: values.jobId,
          type: values.type,
          userId: user.id,
        },
        user.authToken
      );

      const VFUpdateVariables = vfUpdateVariables(sessionSlug, initValues, apiKey)
      VFUpdateVariables.then(() => {
        let VFAnswers = vfInteract(sessionSlug, { type: 'launch' }, apiKey)
        VFAnswers.then((responses) => {
          registerBotResponses(responses, values.jobId, values.type)
          setIsWaitingForResponse(false)
          vfFetchState(sessionSlug, apiKey)
        })
      })
    }, [activityConvs, user]
  );

  const sendToVF = useCallback(
    async (jobId, type, userText, apiKey, shouldSave = true) => {
      setIsWaitingForResponse(true)

      let score = undefined
      let immersion_completed_count = undefined
      let responses
      let body = { type: "text", payload: userText.payload }
      if ( userText.event ) {
        body = {type: "choice", payload: userText.event.request.payload}
      }
      const sessionSlug = activityConvs[`${jobId}/${type}`].sessionSlug
      responses = await vfInteract(
        sessionSlug,
        body,
        apiKey
      );
      await vfFetchState(sessionSlug, apiKey).then(async (response) => {
        if ( response.variables.CIAO_quizz_current_question_index === 7 ) {
          score = Math.round(response.variables.CIAO_quizz_score * 100 / 18)
          const newHistory = await addAction({type: "QUIZZ_ENDED", values: {jobId, score, title: activityConvs[`${jobId}/${type}`].title}})
          await updateLuxMetiersVariables(user.id, newHistory, itemsPro)
        }
        if ( response.variables.CIAO_user_responses?.scenario?.length >= 1 ) {
          const num_scenario = response.variables.CIAO_user_responses.scenario.length
          const no_step_current = response.variables.CIAO_user_responses.scenario[num_scenario - 1 ].step1 === undefined
          const step3_current = response.variables.CIAO_user_responses.scenario[num_scenario - 1 ].step3 !== undefined
          immersion_completed_count = num_scenario
          if ( no_step_current )
            addAction({type: "GRIT_AND_FIT_SCENARIO_STARTED", values: {jobId, num_scenario, title: activityConvs[`${jobId}/${type}`].title}})
          if ( step3_current ) {
            const newHistory = await addAction({type: "GRIT_AND_FIT_SCENARIO_ENDED", values: {jobId, num_scenario, title: activityConvs[`${jobId}/${type}`].title}})
            await updateLuxMetiersVariables(user.id, newHistory, itemsPro)
          } else {
            immersion_completed_count -= 1
          }
        }
      })
      if ( shouldSave ) {
        const key = Math.random().toString(36).substring(2, 15)
        const quizzResponse = userText.payload
        const payload = { sender: "user", content: userText.payload, id: key, type: "text", quizzResponse }
        dispatchActivity({
          type: "ADD_MSG",
          jobId,
          activityType: type,
          payload,
          score
        });

        await addMessage(
          {
            jobId,
            type,
            message: payload,
            score,
            immersion_completed_count
          },
          user.authToken
        );
      }
      setIsWaitingForResponse(false)
      registerBotResponses(responses, jobId, type)
    }, [activityConvs, user]
  );

  async function registerBotResponses(responses, jobId, type) {
    let previousId = null
    for (const b of responses) {
      if (b.type === "text") {
        previousId = b.time
        const payload = { from: "bot", content: b.payload.message, referrer_message_id: null, id: previousId, type: "text" }
        dispatchActivity({
          type: "ADD_MSG",
          jobId,
          activityType: type,
          payload
        });

        await addMessage(
          {
            jobId,
            type,
            message: payload
          },
          user.authToken
        );
      } else if (b.type === "choice" && b.payload.buttons.length > 0) {
        const payload = { from: "bot", content: b.payload.buttons, id: b.time+50, referrer_message_id: previousId, type: "button" }
        dispatchActivity({
          type: "ADD_MSG",
          jobId,
          activityType: type,
          payload
        });

        await addMessage(
          {
            jobId,
            type,
            message: payload
          },
          user.authToken
        );
      }
    };
  }

  const value = useMemo(
    () => ({ activityConvs, initSessionActivity, sendToVF, resetSession, isWaitingForResponse }),
    [activityConvs, initSessionActivity, sendToVF, resetSession, isWaitingForResponse, user]
  );

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
}
